import { useLocation, matchPath } from 'react-router-dom';

// ----------------------------------------------------------------------

// Helper to check if a path matches exactly
const isExactMatch = (path, pathname) =>
  path ? !!matchPath({ path, end: true }, pathname) : false;

// Helper to recursively check if any child is active
const isAnyChildActive = (children, pathname) => {
  if (!children || !children.length) return false;
  return children.some((child) => {
    if (isExactMatch(child.path, pathname)) return true;
    if (child.children) return isAnyChildActive(child.children, pathname);
    return false;
  });
};

export function useActiveLink(itemOrPath, deep = true) {
  const { pathname } = useLocation();

  // Backward compatibility if only string path is passed
  if (typeof itemOrPath === 'string') {
    const normalActive = itemOrPath ? !!matchPath({ path: itemOrPath, end: true }, pathname) : false;
    const deepActive = itemOrPath ? !!matchPath({ path: itemOrPath, end: false }, pathname) : false;
    return deep ? deepActive : normalActive;
  }

  if (!itemOrPath) return false;

  const { path, children } = itemOrPath;

  if (itemOrPath.exact) {
    return isExactMatch(path, pathname);
  }

  // 1. Exact match for the current item
  if (isExactMatch(path, pathname)) {
    return true;
  }

  // 1.5 Custom routing for detached menus (Client and Vendor)
  // These menus don't share a base URL prefix with their add/edit screens
  // (e.g., ListClient vs ClientMst), so normal prefix matching fails.
  if (path) {
    if (path.includes('/masters/ListClient') && pathname.includes('/masters/') && pathname.includes('Client')) return true;
    if (path.includes('/masters/ListVendor') && pathname.includes('/masters/') && pathname.includes('Vendor')) return true;

    // Explicitly link TA routes that act as "My Requisitions" screens
    if (path.endsWith('/dashboard/MyRequisitions')) {
      if (
        pathname.includes('/talentAcquisition/NewRequirement') ||
        pathname.includes('/talentAcquisition/EditReq') ||
        pathname.includes('/talentAcquisition/DetailsReq') ||
        pathname.includes('/talentAcquisition/ReuseReq') ||
        pathname.includes('/talentAcquisition/CanReqManagerList')
      ) {
        return true;
      }
    }
  }

  // 2. Check if any child is active (recursive exact match)
  if (children && children.length > 0) {
    if (isAnyChildActive(children, pathname)) {
      return true;
    }
  }

  // 3. Fallback for dynamic routes (like NewCandidate, Edit, etc.) that are not in children.
  // We use prefix matching, BUT we exclude known conflicting paths.
  if (path) {
    const isPrefixMatch = !!matchPath({ path, end: false }, pathname);

    // Prevent "Master Data" from incorrectly highlighting for "Client" or "Vendor" top-level menus
    if (isPrefixMatch && path.includes('/dashboard/masters')) {
      const isConflictPath =
        pathname.includes('/masters/ListClient') ||
        pathname.includes('/masters/EditClient') ||
        pathname.includes('/masters/DetailClient') ||
        pathname.includes('/masters/ClientMst') ||
        pathname.includes('/masters/ListVendor') ||
        pathname.includes('/masters/VendorMst') ||
        pathname.includes('/masters/EditVendor') ||
        pathname.includes('/masters/DetailVendor');

      if (isConflictPath) {
        return false;
      }
    }

    // Prevent "Talent Acquisition" from incorrectly highlighting for "My Requisitions" screens
    if (isPrefixMatch && path.endsWith('/dashboard/talentAcquisition')) {
      const isMyReqPath =
        pathname.includes('/talentAcquisition/NewRequirement') ||
        pathname.includes('/talentAcquisition/EditReq') ||
        pathname.includes('/talentAcquisition/DetailsReq') ||
        pathname.includes('/talentAcquisition/ReuseReq') ||
        pathname.includes('/talentAcquisition/CanReqManagerList');

      if (isMyReqPath) {
        return false;
      }
    }

    // For any other prefix match, consider the menu active.
    if (isPrefixMatch) {
      return true;
    }
  }

  // 4. Strict rule: Parent is only active if exact match, child matches, or safe prefix match.
  // We avoid generic startsWith/includes matching to prevent wrong parent highlighting.
  return false;
}
